/**
 * Routing and the authentication gate.
 *
 * Every route except the login page requires a session. The gate renders the
 * login page rather than redirecting, so a signed-out user who reloads a deep
 * link lands back on that page once they sign in.
 */

import { Navigate, Route, Routes } from 'react-router-dom';

import { Layout } from './components/Layout';
import { Spinner } from './components/ui';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { BotsProvider } from './hooks/useBots';
import { AiProvider } from './pages/AiProvider';
import { Analytics } from './pages/Analytics';
import { Embed } from './pages/Embed';
import { Contacts } from './pages/Contacts';
import { DataSources } from './pages/DataSources';
import { Email } from './pages/Email';
import { Inbox } from './pages/Inbox';
import { Latency } from './pages/Latency';
import { Login } from './pages/Login';
import { Overview } from './pages/Overview';
import { Preview } from './pages/Preview';
import { Reports } from './pages/Reports';
import { Settings } from './pages/Settings';
import { Suggestions } from './pages/Suggestions';
import { SystemCheck } from './pages/SystemCheck';

function AuthenticatedApp(): JSX.Element {
  return (
    <BotsProvider>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Navigate to="/inbox" replace />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/data-sources" element={<DataSources />} />
          <Route path="/suggestions" element={<Suggestions />} />
          <Route path="/preview" element={<Preview />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/embed" element={<Embed />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/overview" element={<Overview />} />
          <Route path="/ai-provider" element={<AiProvider />} />
          <Route path="/email" element={<Email />} />
          <Route path="/system-check" element={<SystemCheck />} />
          <Route path="/latency" element={<Latency />} />
          <Route path="*" element={<Navigate to="/inbox" replace />} />
        </Route>
      </Routes>
    </BotsProvider>
  );
}

function Gate(): JSX.Element {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Restoring session…" />
      </div>
    );
  }
  return user ? <AuthenticatedApp /> : <Login />;
}

export function App(): JSX.Element {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}
